import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { scrollableTableWrapStyles, scrollableTableSizing } from '../styles/scrollableTable';
import { FiEdit2, FiTrash2, FiPlus, FiX, FiSave } from 'react-icons/fi';
import { supabase } from '../../config/supabase';
import { useLanguage } from '../../context/LanguageContext';

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TabsContainer = styled.div`
  background: white;
  border-radius: 16px 16px 0 0;
  border: 1px solid #e2e8f0;
  border-bottom: none;
  padding: 0;
  overflow-x: auto;
`;

const TabsList = styled.div`
  display: flex;
  gap: 0;
  min-width: max-content;
`;

const Tab = styled.button`
  padding: 16px 24px;
  border: none;
  background: ${props => props.active ? '#5efba9' : 'transparent'};
  color: ${props => props.active ? '#0f172a' : '#6b7280'};
  font-weight: ${props => props.active ? '600' : '500'};
  font-size: 14px;
  cursor: pointer;
  border-bottom: 2px solid ${props => props.active ? '#5efba9' : 'transparent'};
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${props => props.active ? '#5efba9' : '#f9fafb'};
  }
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 0 0 16px 16px;
  padding: 32px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
  border: 1px solid #e2e8f0;
  border-top: none;

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px;
`;

const SectionSubtitle = styled.p`
  margin: 0 0 24px;
  color: #6b7280;
  font-size: 14px;
`;

const TableScrollArea = styled.div`
  ${scrollableTableWrapStyles}
  margin-top: 16px;
`;

const Table = styled.table`
  ${scrollableTableSizing}
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid #e5e7eb;

  &:hover {
    background: #f9fafb;
  }
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #111827;
`;

const ActionButton = styled.button`
  padding: 8px 12px;
  border: none;
  background: ${props => props.variant === 'danger' ? '#fef2f2' : '#f0fdf4'};
  color: ${props => props.variant === 'danger' ? '#dc2626' : '#059669'};
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.variant === 'danger' ? '#fee2e2' : '#dcfce7'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddButton = styled.button`
  padding: 10px 20px;
  background: #5efba9;
  color: #0f172a;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  margin-bottom: 16px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(94, 251, 169, 0.3);
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const CloseButton = styled.button`
  border: none;
  background: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #111827;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
`;

const Input = styled.input`
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font-size: 15px;
  color: #111827;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.25);
    outline: none;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Textarea = styled.textarea`
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font-size: 15px;
  color: #111827;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.25);
    outline: none;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font-size: 15px;
  color: #111827;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.25);
    outline: none;
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const SaveButton = styled.button`
  padding: 12px 24px;
  background: #5efba9;
  color: #0f172a;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(94, 251, 169, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  background: white;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`;

const StatusMessage = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  background: ${props => props.type === 'error' ? '#fef2f2' : '#f0fdf4'};
  color: ${props => props.type === 'error' ? '#dc2626' : '#059669'};
  border: 1px solid ${props => props.type === 'error' ? '#fecaca' : '#bbf7d0'};
  font-size: 14px;
`;

const Loader = styled.div`
  padding: 40px 0;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
`;

export default function SuperAdminSettings() {
  const { isArabic } = useLanguage();
  const [activeTab, setActiveTab] = useState('media');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  // Media Links State
  const [mediaLinks, setMediaLinks] = useState(null);
  const [mediaForm, setMediaForm] = useState({
    whatsapp_url: '',
    instagram_url: '',
    x_url: '',
  });

  // Privacy Policy State
  const [privacySections, setPrivacySections] = useState([]);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [editingPrivacy, setEditingPrivacy] = useState(null);

  // Terms State
  const [termsSections, setTermsSections] = useState([]);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [editingTerms, setEditingTerms] = useState(null);

  // FAQ State
  const [faqItems, setFaqItems] = useState([]);
  const [showFaqModal, setShowFaqModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);

  // Contact Info State
  const [contactInfo, setContactInfo] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);

  // Load data based on active tab
  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setStatus(null);

    try {
      if (activeTab === 'media') {
        const { data, error } = await supabase
          .from('media')
          .select('*')
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        setMediaLinks(data);
        if (data) {
          setMediaForm({
            whatsapp_url: data.whatsapp_url || '',
            instagram_url: data.instagram_url || '',
            x_url: data.x_url || '',
          });
        }
      } else if (activeTab === 'privacy') {
        const { data, error } = await supabase
          .from('privacy_policy')
          .select('*')
          .order('section_order', { ascending: true });

        if (error) throw error;
        setPrivacySections(data || []);
      } else if (activeTab === 'terms') {
        const { data, error } = await supabase
          .from('terms_and_conditions')
          .select('*')
          .order('section_order', { ascending: true });

        if (error) throw error;
        setTermsSections(data || []);
      } else if (activeTab === 'faq') {
        const { data, error } = await supabase
          .from('faq_items')
          .select('*')
          .order('display_order', { ascending: true });

        if (error) throw error;
        setFaqItems(data || []);
      } else if (activeTab === 'contact') {
        const { data, error } = await supabase
          .from('contact_info')
          .select('*')
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        setContactInfo(data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setStatus({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Media Links Handlers
  const handleSaveMedia = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    try {
      const payload = {
        whatsapp_url: mediaForm.whatsapp_url?.trim() || null,
        instagram_url: mediaForm.instagram_url?.trim() || null,
        x_url: mediaForm.x_url?.trim() || null,
      };

      if (mediaLinks?.id) {
        const { error } = await supabase
          .from('media')
          .update(payload)
          .eq('id', mediaLinks.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('media')
          .insert(payload);

        if (error) throw error;
      }

      setStatus({ type: 'success', message: 'Media links saved successfully!' });
      loadData();
    } catch (error) {
      console.error('Error saving media:', error);
      setStatus({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Privacy Policy Handlers
  const handleSavePrivacy = async (formData) => {
    setLoading(true);
    setStatus(null);

    try {
      if (editingPrivacy) {
        const { error } = await supabase
          .from('privacy_policy')
          .update(formData)
          .eq('id', editingPrivacy.id);

        if (error) throw error;
        setStatus({ type: 'success', message: 'Privacy section updated!' });
      } else {
        const { error } = await supabase
          .from('privacy_policy')
          .insert(formData);

        if (error) throw error;
        setStatus({ type: 'success', message: 'Privacy section added!' });
      }

      setShowPrivacyModal(false);
      setEditingPrivacy(null);
      loadData();
    } catch (error) {
      console.error('Error saving privacy:', error);
      setStatus({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePrivacy = async (id) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('privacy_policy')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setStatus({ type: 'success', message: 'Section deleted!' });
      loadData();
    } catch (error) {
      console.error('Error deleting:', error);
      setStatus({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Similar handlers for Terms, FAQ, Contact...
  const handleSaveTerms = async (formData) => {
    setLoading(true);
    setStatus(null);

    try {
      if (editingTerms) {
        const { error } = await supabase
          .from('terms_and_conditions')
          .update(formData)
          .eq('id', editingTerms.id);

        if (error) throw error;
        setStatus({ type: 'success', message: 'Terms section updated!' });
      } else {
        const { error } = await supabase
          .from('terms_and_conditions')
          .insert(formData);

        if (error) throw error;
        setStatus({ type: 'success', message: 'Terms section added!' });
      }

      setShowTermsModal(false);
      setEditingTerms(null);
      loadData();
    } catch (error) {
      console.error('Error saving terms:', error);
      setStatus({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTerms = async (id) => {
    if (!window.confirm('Are you sure?')) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('terms_and_conditions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setStatus({ type: 'success', message: 'Section deleted!' });
      loadData();
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFaq = async (formData) => {
    setLoading(true);
    setStatus(null);

    try {
      if (editingFaq) {
        const { error } = await supabase
          .from('faq_items')
          .update(formData)
          .eq('id', editingFaq.id);

        if (error) throw error;
        setStatus({ type: 'success', message: 'FAQ updated!' });
      } else {
        const { error } = await supabase
          .from('faq_items')
          .insert(formData);

        if (error) throw error;
        setStatus({ type: 'success', message: 'FAQ added!' });
      }

      setShowFaqModal(false);
      setEditingFaq(null);
      loadData();
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFaq = async (id) => {
    if (!window.confirm('Are you sure?')) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('faq_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setStatus({ type: 'success', message: 'FAQ deleted!' });
      loadData();
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContact = async (formData) => {
    setLoading(true);
    setStatus(null);

    try {
      if (contactInfo?.id) {
        const { error } = await supabase
          .from('contact_info')
          .update(formData)
          .eq('id', contactInfo.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('contact_info')
          .insert(formData);

        if (error) throw error;
      }

      setStatus({ type: 'success', message: 'Contact info saved!' });
      setShowContactModal(false);
      loadData();
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper>
      <TabsContainer>
        <TabsList>
          <Tab active={activeTab === 'media'} onClick={() => setActiveTab('media')}>
            {isArabic ? 'روابط الوسائط' : 'Media Links'}
          </Tab>
          <Tab active={activeTab === 'privacy'} onClick={() => setActiveTab('privacy')}>
            {isArabic ? 'سياسة الخصوصية' : 'Privacy Policy'}
          </Tab>
          <Tab active={activeTab === 'terms'} onClick={() => setActiveTab('terms')}>
            {isArabic ? 'الشروط والأحكام' : 'Terms & Conditions'}
          </Tab>
          <Tab active={activeTab === 'faq'} onClick={() => setActiveTab('faq')}>
            FAQ
          </Tab>
          <Tab active={activeTab === 'contact'} onClick={() => setActiveTab('contact')}>
            {isArabic ? 'معلومات التواصل' : 'Contact Info'}
          </Tab>
        </TabsList>
      </TabsContainer>

      <ContentCard>
        {status && <StatusMessage type={status.type}>{status.message}</StatusMessage>}

        {/* MEDIA LINKS TAB */}
        {activeTab === 'media' && (
          <>
            <SectionTitle>{isArabic ? 'روابط التواصل والوسائط' : 'Media & Social Links'}</SectionTitle>
            <SectionSubtitle>{isArabic ? 'إدارة روابط التواصل الاجتماعي المعروضة في التذييل' : 'Manage social media links displayed in the footer'}</SectionSubtitle>

            {loading ? (
              <Loader>{isArabic ? 'جارٍ التحميل...' : 'Loading...'}</Loader>
            ) : (
              <Form onSubmit={handleSaveMedia}>
                <Field>
                  <Label>WhatsApp URL</Label>
                  <Input
                    type="url"
                    placeholder="https://api.whatsapp.com/send?phone=..."
                    value={mediaForm.whatsapp_url}
                    onChange={(e) => setMediaForm({ ...mediaForm, whatsapp_url: e.target.value })}
                  />
                </Field>

                <Field>
                  <Label>Instagram URL</Label>
                  <Input
                    type="url"
                    placeholder="https://www.instagram.com/..."
                    value={mediaForm.instagram_url}
                    onChange={(e) => setMediaForm({ ...mediaForm, instagram_url: e.target.value })}
                  />
                </Field>

                <Field>
                  <Label>X (Twitter) URL</Label>
                  <Input
                    type="url"
                    placeholder="https://x.com/..."
                    value={mediaForm.x_url}
                    onChange={(e) => setMediaForm({ ...mediaForm, x_url: e.target.value })}
                  />
                </Field>

                <ButtonGroup>
                  <SaveButton type="submit" disabled={loading}>
                    <FiSave /> {isArabic ? 'حفظ روابط الوسائط' : 'Save Media Links'}
                  </SaveButton>
                </ButtonGroup>
              </Form>
            )}
          </>
        )}

        {/* PRIVACY POLICY TAB */}
        {activeTab === 'privacy' && (
          <>
            <SectionTitle>{isArabic ? 'أقسام سياسة الخصوصية' : 'Privacy Policy Sections'}</SectionTitle>
            <SectionSubtitle>{isArabic ? 'إدارة محتوى سياسة الخصوصية' : 'Manage privacy policy content'}</SectionSubtitle>

            <AddButton onClick={() => { setEditingPrivacy(null); setShowPrivacyModal(true); }}>
              <FiPlus /> {isArabic ? 'إضافة قسم' : 'Add Section'}
            </AddButton>

            {loading ? (
              <Loader>{isArabic ? 'جارٍ التحميل...' : 'Loading...'}</Loader>
            ) : privacySections.length === 0 ? (
              <EmptyState>{isArabic ? 'لا توجد أقسام لسياسة الخصوصية بعد. اضغط "إضافة قسم" لإنشاء قسم.' : 'No privacy policy sections yet. Click "Add Section" to create one.'}</EmptyState>
            ) : (
              <TableScrollArea>
                <Table>
                  <Thead>
                    <Tr>
                      <Th>Order</Th>
                      <Th>Title (EN)</Th>
                      <Th>Title (AR)</Th>
                      <Th>Active</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {privacySections.map((section) => (
                      <Tr key={section.id}>
                        <Td>{section.section_order}</Td>
                        <Td>{section.title_en}</Td>
                        <Td>{section.title_ar}</Td>
                        <Td>{section.is_active ? '✓' : '✗'}</Td>
                        <Td>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <ActionButton onClick={() => { setEditingPrivacy(section); setShowPrivacyModal(true); }}>
                              <FiEdit2 /> Edit
                            </ActionButton>
                            <ActionButton variant="danger" onClick={() => handleDeletePrivacy(section.id)}>
                              <FiTrash2 /> Delete
                            </ActionButton>
                          </div>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableScrollArea>
            )}

            {showPrivacyModal && (
              <PrivacyModal
                section={editingPrivacy}
                onSave={handleSavePrivacy}
                onClose={() => { setShowPrivacyModal(false); setEditingPrivacy(null); }}
              />
            )}
          </>
        )}

        {/* TERMS TAB */}
        {activeTab === 'terms' && (
          <>
            <SectionTitle>{isArabic ? 'أقسام الشروط والأحكام' : 'Terms & Conditions Sections'}</SectionTitle>
            <SectionSubtitle>{isArabic ? 'إدارة محتوى الشروط والأحكام' : 'Manage terms and conditions content'}</SectionSubtitle>

            <AddButton onClick={() => { setEditingTerms(null); setShowTermsModal(true); }}>
              <FiPlus /> {isArabic ? 'إضافة قسم' : 'Add Section'}
            </AddButton>

            {loading ? (
              <Loader>{isArabic ? 'جارٍ التحميل...' : 'Loading...'}</Loader>
            ) : termsSections.length === 0 ? (
              <EmptyState>{isArabic ? 'لا توجد أقسام شروط بعد.' : 'No terms sections yet.'}</EmptyState>
            ) : (
              <TableScrollArea>
                <Table>
                  <Thead>
                    <Tr>
                      <Th>Order</Th>
                      <Th>Title (EN)</Th>
                      <Th>Title (AR)</Th>
                      <Th>Active</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {termsSections.map((section) => (
                      <Tr key={section.id}>
                        <Td>{section.section_order}</Td>
                        <Td>{section.title_en}</Td>
                        <Td>{section.title_ar}</Td>
                        <Td>{section.is_active ? '✓' : '✗'}</Td>
                        <Td>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <ActionButton onClick={() => { setEditingTerms(section); setShowTermsModal(true); }}>
                              <FiEdit2 /> Edit
                            </ActionButton>
                            <ActionButton variant="danger" onClick={() => handleDeleteTerms(section.id)}>
                              <FiTrash2 /> Delete
                            </ActionButton>
                          </div>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableScrollArea>
            )}

            {showTermsModal && (
              <TermsModal
                section={editingTerms}
                onSave={handleSaveTerms}
                onClose={() => { setShowTermsModal(false); setEditingTerms(null); }}
              />
            )}
          </>
        )}

        {/* FAQ TAB */}
        {activeTab === 'faq' && (
          <>
            <SectionTitle>{isArabic ? 'عناصر الأسئلة الشائعة' : 'FAQ Items'}</SectionTitle>
            <SectionSubtitle>{isArabic ? 'إدارة الأسئلة الشائعة' : 'Manage frequently asked questions'}</SectionSubtitle>

            <AddButton onClick={() => { setEditingFaq(null); setShowFaqModal(true); }}>
              <FiPlus /> {isArabic ? 'إضافة سؤال شائع' : 'Add FAQ'}
            </AddButton>

            {loading ? (
              <Loader>{isArabic ? 'جارٍ التحميل...' : 'Loading...'}</Loader>
            ) : faqItems.length === 0 ? (
              <EmptyState>{isArabic ? 'لا توجد أسئلة شائعة بعد.' : 'No FAQ items yet.'}</EmptyState>
            ) : (
              <TableScrollArea>
                <Table>
                  <Thead>
                    <Tr>
                      <Th>Order</Th>
                      <Th>Question (EN)</Th>
                      <Th>Category</Th>
                      <Th>Active</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {faqItems.map((item) => (
                      <Tr key={item.id}>
                        <Td>{item.display_order}</Td>
                        <Td>{item.question_en}</Td>
                        <Td>{item.category}</Td>
                        <Td>{item.is_active ? '✓' : '✗'}</Td>
                        <Td>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <ActionButton onClick={() => { setEditingFaq(item); setShowFaqModal(true); }}>
                              <FiEdit2 /> Edit
                            </ActionButton>
                            <ActionButton variant="danger" onClick={() => handleDeleteFaq(item.id)}>
                              <FiTrash2 /> Delete
                            </ActionButton>
                          </div>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableScrollArea>
            )}

            {showFaqModal && (
              <FaqModal
                item={editingFaq}
                onSave={handleSaveFaq}
                onClose={() => { setShowFaqModal(false); setEditingFaq(null); }}
              />
            )}
          </>
        )}

        {/* CONTACT INFO TAB */}
        {activeTab === 'contact' && (
          <>
            <SectionTitle>{isArabic ? 'معلومات التواصل' : 'Contact Information'}</SectionTitle>
            <SectionSubtitle>{isArabic ? 'إدارة محتوى صفحة التواصل' : 'Manage contact page content'}</SectionSubtitle>

            {loading ? (
              <Loader>{isArabic ? 'جارٍ التحميل...' : 'Loading...'}</Loader>
            ) : contactInfo ? (
              <div>
                <p><strong>Email:</strong> {contactInfo.email}</p>
                <p><strong>Phone:</strong> {contactInfo.phone}</p>
                <p><strong>Address (EN):</strong> {contactInfo.address_en}</p>
                <p><strong>Address (AR):</strong> {contactInfo.address_ar}</p>
                <AddButton onClick={() => setShowContactModal(true)}>
                  <FiEdit2 /> {isArabic ? 'تعديل معلومات التواصل' : 'Edit Contact Info'}
                </AddButton>
              </div>
            ) : (
              <div>
                <EmptyState>{isArabic ? 'لم يتم تعيين معلومات تواصل بعد.' : 'No contact information set.'}</EmptyState>
                <AddButton onClick={() => setShowContactModal(true)}>
                  <FiPlus /> {isArabic ? 'إضافة معلومات تواصل' : 'Add Contact Info'}
                </AddButton>
              </div>
            )}

            {showContactModal && (
              <ContactModal
                contact={contactInfo}
                onSave={handleSaveContact}
                onClose={() => setShowContactModal(false)}
              />
            )}
          </>
        )}
      </ContentCard>
    </PageWrapper>
  );
}

// Modal Components
function PrivacyModal({ section, onSave, onClose }) {
  const [formData, setFormData] = useState({
    section_order: section?.section_order || 1,
    title_en: section?.title_en || '',
    title_ar: section?.title_ar || '',
    content_en: section?.content_en || '',
    content_ar: section?.content_ar || '',
    is_active: section?.is_active ?? true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{section ? 'Edit' : 'Add'} Privacy Section</ModalTitle>
          <CloseButton onClick={onClose}><FiX /></CloseButton>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          <Field>
            <Label>Section Order</Label>
            <Input
              type="number"
              value={formData.section_order}
              onChange={(e) => setFormData({ ...formData, section_order: parseInt(e.target.value) })}
              required
            />
          </Field>

          <Field>
            <Label>Title (English)</Label>
            <Input
              type="text"
              value={formData.title_en}
              onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
              required
            />
          </Field>

          <Field>
            <Label>Title (Arabic)</Label>
            <Input
              type="text"
              value={formData.title_ar}
              onChange={(e) => setFormData({ ...formData, title_ar: e.target.value })}
              required
            />
          </Field>

          <Field>
            <Label>Content (English)</Label>
            <Textarea
              value={formData.content_en}
              onChange={(e) => setFormData({ ...formData, content_en: e.target.value })}
              required
            />
          </Field>

          <Field>
            <Label>Content (Arabic)</Label>
            <Textarea
              value={formData.content_ar}
              onChange={(e) => setFormData({ ...formData, content_ar: e.target.value })}
              required
            />
          </Field>

          <Field>
            <Label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Checkbox
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              Active
            </Label>
          </Field>

          <ButtonGroup>
            <CancelButton type="button" onClick={onClose}>Cancel</CancelButton>
            <SaveButton type="submit"><FiSave /> Save</SaveButton>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </Modal>
  );
}

function TermsModal({ section, onSave, onClose }) {
  const [formData, setFormData] = useState({
    section_order: section?.section_order || 1,
    title_en: section?.title_en || '',
    title_ar: section?.title_ar || '',
    content_en: section?.content_en || '',
    content_ar: section?.content_ar || '',
    is_active: section?.is_active ?? true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{section ? 'Edit' : 'Add'} Terms Section</ModalTitle>
          <CloseButton onClick={onClose}><FiX /></CloseButton>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          <Field>
            <Label>Section Order</Label>
            <Input
              type="number"
              value={formData.section_order}
              onChange={(e) => setFormData({ ...formData, section_order: parseInt(e.target.value) })}
              required
            />
          </Field>

          <Field>
            <Label>Title (English)</Label>
            <Input
              type="text"
              value={formData.title_en}
              onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
              required
            />
          </Field>

          <Field>
            <Label>Title (Arabic)</Label>
            <Input
              type="text"
              value={formData.title_ar}
              onChange={(e) => setFormData({ ...formData, title_ar: e.target.value })}
              required
            />
          </Field>

          <Field>
            <Label>Content (English)</Label>
            <Textarea
              value={formData.content_en}
              onChange={(e) => setFormData({ ...formData, content_en: e.target.value })}
              required
            />
          </Field>

          <Field>
            <Label>Content (Arabic)</Label>
            <Textarea
              value={formData.content_ar}
              onChange={(e) => setFormData({ ...formData, content_ar: e.target.value })}
              required
            />
          </Field>

          <Field>
            <Label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Checkbox
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              Active
            </Label>
          </Field>

          <ButtonGroup>
            <CancelButton type="button" onClick={onClose}>Cancel</CancelButton>
            <SaveButton type="submit"><FiSave /> Save</SaveButton>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </Modal>
  );
}

function FaqModal({ item, onSave, onClose }) {
  const [formData, setFormData] = useState({
    display_order: item?.display_order || 1,
    question_en: item?.question_en || '',
    question_ar: item?.question_ar || '',
    answer_en: item?.answer_en || '',
    answer_ar: item?.answer_ar || '',
    category: item?.category || 'general',
    is_active: item?.is_active ?? true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{item ? 'Edit' : 'Add'} FAQ</ModalTitle>
          <CloseButton onClick={onClose}><FiX /></CloseButton>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          <Field>
            <Label>Display Order</Label>
            <Input
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
              required
            />
          </Field>

          <Field>
            <Label>Category</Label>
            <Select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            >
              <option value="general">General</option>
              <option value="orders">Orders</option>
              <option value="payment">Payment</option>
              <option value="shipping">Shipping</option>
              <option value="returns">Returns</option>
              <option value="selling">Selling</option>
            </Select>
          </Field>

          <Field>
            <Label>Question (English)</Label>
            <Input
              type="text"
              value={formData.question_en}
              onChange={(e) => setFormData({ ...formData, question_en: e.target.value })}
              required
            />
          </Field>

          <Field>
            <Label>Question (Arabic)</Label>
            <Input
              type="text"
              value={formData.question_ar}
              onChange={(e) => setFormData({ ...formData, question_ar: e.target.value })}
              required
            />
          </Field>

          <Field>
            <Label>Answer (English)</Label>
            <Textarea
              value={formData.answer_en}
              onChange={(e) => setFormData({ ...formData, answer_en: e.target.value })}
              required
            />
          </Field>

          <Field>
            <Label>Answer (Arabic)</Label>
            <Textarea
              value={formData.answer_ar}
              onChange={(e) => setFormData({ ...formData, answer_ar: e.target.value })}
              required
            />
          </Field>

          <Field>
            <Label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Checkbox
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              Active
            </Label>
          </Field>

          <ButtonGroup>
            <CancelButton type="button" onClick={onClose}>Cancel</CancelButton>
            <SaveButton type="submit"><FiSave /> Save</SaveButton>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </Modal>
  );
}

function ContactModal({ contact, onSave, onClose }) {
  const [formData, setFormData] = useState({
    email: contact?.email || '',
    phone: contact?.phone || '',
    address_en: contact?.address_en || '',
    address_ar: contact?.address_ar || '',
    working_hours_en: contact?.working_hours_en || '',
    working_hours_ar: contact?.working_hours_ar || '',
    whatsapp_url: contact?.whatsapp_url || '',
    instagram_url: contact?.instagram_url || '',
    twitter_url: contact?.twitter_url || '',
    facebook_url: contact?.facebook_url || '',
    is_active: contact?.is_active ?? true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{contact ? 'Edit' : 'Add'} Contact Info</ModalTitle>
          <CloseButton onClick={onClose}><FiX /></CloseButton>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          <Field>
            <Label>Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </Field>

          <Field>
            <Label>Phone</Label>
            <Input
              type="text"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </Field>

          <Field>
            <Label>Address (English)</Label>
            <Input
              type="text"
              value={formData.address_en}
              onChange={(e) => setFormData({ ...formData, address_en: e.target.value })}
            />
          </Field>

          <Field>
            <Label>Address (Arabic)</Label>
            <Input
              type="text"
              value={formData.address_ar}
              onChange={(e) => setFormData({ ...formData, address_ar: e.target.value })}
            />
          </Field>

          <Field>
            <Label>Working Hours (English)</Label>
            <Input
              type="text"
              value={formData.working_hours_en}
              onChange={(e) => setFormData({ ...formData, working_hours_en: e.target.value })}
            />
          </Field>

          <Field>
            <Label>Working Hours (Arabic)</Label>
            <Input
              type="text"
              value={formData.working_hours_ar}
              onChange={(e) => setFormData({ ...formData, working_hours_ar: e.target.value })}
            />
          </Field>

          <Field>
            <Label>WhatsApp URL</Label>
            <Input
              type="url"
              value={formData.whatsapp_url}
              onChange={(e) => setFormData({ ...formData, whatsapp_url: e.target.value })}
            />
          </Field>

          <Field>
            <Label>Instagram URL</Label>
            <Input
              type="url"
              value={formData.instagram_url}
              onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
            />
          </Field>

          <Field>
            <Label>Twitter URL</Label>
            <Input
              type="url"
              value={formData.twitter_url}
              onChange={(e) => setFormData({ ...formData, twitter_url: e.target.value })}
            />
          </Field>

          <Field>
            <Label>Facebook URL</Label>
            <Input
              type="url"
              value={formData.facebook_url}
              onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
            />
          </Field>

          <Field>
            <Label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Checkbox
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              Active
            </Label>
          </Field>

          <ButtonGroup>
            <CancelButton type="button" onClick={onClose}>Cancel</CancelButton>
            <SaveButton type="submit"><FiSave /> Save</SaveButton>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </Modal>
  );
}
